import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { action, observable, runInAction, makeObservable } from "mobx";
import { projectsDataStore } from "Stores";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { filter, orderBy } from "lodash";
import { dayjs } from "Config/dayjs";
import { pdfViewerStore } from "Stores/PdfViewerStore";

export class BcfViewerStore extends CoreStore {
  bcfData: API_BcfFileContainer = null;
  topics: API_BcfTopic[] = [];
  filters: API_BcfExtensions = null;
  activeTopic: API_BcfTopic = null;
  activeComments: API_BcfComment[] = [];
  activeIcfFiles: API_IfcFile[] = [];
  authorsFilters: string[] = [];
  activeCommentSnapshot: string = null;
  commentsSortFilter: {
    ["key"]: keyof API_BcfComment;
    ["direction"]: "asc" | "desc";
  } = null;
  activeFileId: number = null;

  constructor() {
    super();

    makeObservable(this, {
      bcfData: observable,
      topics: observable,
      filters: observable,
      activeTopic: observable,
      activeComments: observable,
      activeIcfFiles: observable,
      authorsFilters: observable,
      activeCommentSnapshot: observable,
      commentsSortFilter: observable,
      activeFileId: observable,
      clear: action.bound,
      clearActiveTopic: action.bound,
      fetchCommentPreview: action.bound,
      setActiveFileId: action.bound,
      setCommentsSortFilter: action.bound,
      setAuthorsFilter: action.bound,
      filterCommentsBy: action.bound,
      sortCommentsBy: action.bound,
      fetch: action.bound,
      fetchTopicById: action.bound,
    });

    this.setCommentsSortFilter({ key: "date", direction: "desc" });
  }

  url(fileId: number) {
    return API_URL.BCF.getBcfFileContainer(projectsDataStore.projectId, fileId);
  }

  clear() {
    this.bcfData = null;
    this.filters = null;
    this.topics = [];
    this.activeFileId = null;
    this.clearActiveTopic();
  }

  clearActiveTopic() {
    this.activeTopic = null;
    this.activeComments = [];
    this.authorsFilters = [];
    this.activeCommentSnapshot = null;
    this.activeIcfFiles = [];
  }

  @handleStoreState()
  async fetch(params?: API_BcfTopicFilter) {
    try {
      const res: API_BcfFileContainer = await API.newData(this.url(this.activeFileId), params);
      if (res) {
        runInAction(() => {
          this.bcfData = res;
          this.topics = res.bcfTopics;
          this.filters = res.bcfExtensions;
        });
      }
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async fetchTopicById(topicId: string) {
    try {
      const res: API_BcfTopic = await API.newData(
        API_URL.BCF.getSingleTopicDetails(projectsDataStore.projectId, this.activeFileId, topicId),
        { ...this.bcfData }
      );

      if (res) {
        this.setCommentsSortFilter({ key: "date", direction: "desc" });
        runInAction(() => {
          this.activeTopic = res;
          this.activeComments = res.comments;
          this.activeIcfFiles = res.ifcFiles;
          this.setAuthorsFilter(res.comments);
        });
      }
    } catch (error) {
      throw error;
    }
  }

  async fetchCommentPreview(commentId: string) {
    try {
      const snapshot = await API.request(
        API_URL.BCF.getSnapshotOfCommentForEasyViewer(projectsDataStore.projectId, this.activeFileId, commentId),
        { method: "POST", responseType: "blob", body: this.activeTopic },
        true
      );
      pdfViewerStore.initByFile(snapshot);
    } catch (err) {
      throw err;
    }
  }

  setActiveFileId(id: number) {
    this.activeFileId = id;
  }

  setCommentsSortFilter(data: { ["key"]: keyof API_BcfComment; ["direction"]: "asc" | "desc" }) {
    this.commentsSortFilter = data;
  }

  setAuthorsFilter(comments: API_BcfComment[]) {
    if (comments) {
      const uniqueValues = Array.from(new Set(comments?.map((c) => c.author)));
      this.authorsFilters = uniqueValues;
    }
  }

  filterCommentsBy(
    key: keyof API_BcfComment = "author",
    value: string,
    sortKey: keyof API_BcfComment = this.commentsSortFilter.key,
    sortDirection: "asc" | "desc" = this.commentsSortFilter.direction
  ) {
    if (value === "all") {
      this.activeComments = this.activeTopic.comments;
      this.sortCommentsBy(sortKey, sortDirection);
    } else {
      this.activeComments = filter(this.activeTopic.comments, (comment) => comment[key] === value);
      this.sortCommentsBy(sortKey, sortDirection);
    }
  }

  sortCommentsBy(key: keyof API_BcfComment, direction: "asc" | "desc") {
    this.setCommentsSortFilter({ key, direction });
    this.activeComments = orderBy(
      this.activeComments,
      (v) => {
        return key === "date" ? dayjs(v.date).format("X") : v[key];
      },
      direction
    );
  }
}

export const bcfViewerStore = new BcfViewerStore();
